/**
 * @file billLineReconcile.js
 * @version 1.5.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-05
 * @modified 2026-10-23
 * @description A bill filled from an uploaded invoice, reconciled line by line
 *              with its POs (Jade, 5 Oct 2026). Pure — Add Bill keeps the
 *              lines; this says which are matched, matches the sure ones, and
 *              lists what is left.
 *
 * Lines in the bill grid carry where they came from:
 *   _fromPdf        read from the uploaded invoice, not yet matched to a PO line
 *   _fromPo         brought in from a PO (one per PO line; a PO-H gives one
 *                   haulage line, `_poHaulageLine`, which has no PO line key)
 *   _pdfMatched     a PO line the invoice has a line for: the invoice's
 *                   quantity and price on the PO line's identity (its PO, PO
 *                   line, item, account), so the bill keeps the link
 *
 * Matched automatically only when it is certain: the same item code with one
 * PO line left to take it (several: the one with the same quantity), else the
 * same words. Never by position — that would put one product's price on
 * another's PO line.
 *
 * Before saving, every line must be reconciled (reconcileState): an invoice
 * line on no PO line, or a PO line the invoice has no line for, must be matched
 * or deleted by the user.
 *
 * 1.3.0 (2026-10-16, Cam — "a direct delivery PO being billed … our PO has
 * the item, and the bill has the item with a price that included the
 * haulage"): a Direct Delivery PO's HAULAGE line (`_poKind: 'haulage'`, the
 * line poHaulageLineService puts after each product — account 320/321, no
 * item) is never matched automatically to a goods line of the invoice: the
 * product line is. Only a charge line (`isChargeLine` — Haul, Carriage,
 * Transport, Fuel surcharge…) may take it, and only when it is the one open
 * Direct Delivery haulage line (a PO-H's line is matched by hand, as
 * before). `isChargeLine` is also what tells a haulier's bill of jobs
 * from a goods bill (utils/billJobLines.isBillOfJobs).
 *
 * 1.4.0 (2026-10-16, Cam — the VAT was not put in): a matched line's tax is
 * the PO line's when it has one, else its item's purchase tax type, else the
 * BILL's (`_billTax` — the line's own VAT %, or the invoice's VAT total as
 * one rate: utils/billReadLines.defaultTaxType). 'NONE' on a PO line is no
 * tax (a PO made here saves it when nobody chose). The bill's side
 * (`_bill.tax_type`) is what the invoice says, not the item's.
 *
 * 1.2.0 (2026-10-16, Cam — Add Bill made simple): a match keeps what the
 * invoice said on the line (`_bill`: quantity, price, words, item, account,
 * tax, unit), so each cell can offer the PO's value or the bill's
 * (utils/billLineFields). What is left to reconcile is a gentle note on the
 * line now, never a reason the bill will not save.
 *
 * 1.1.2 (2026-10-14): a match keeps the invoice line's unit (`bill_unit` —
 * kg / T / a pack), the evidence utils/billUnitConversion reads it by.
 */

import { formatQuantity } from './numberFormatters';
import { isChargeLine } from './chargeLines';
import { defaultTaxType } from './billReadLines';
import { getTaxRate } from './taxTypes';

const EPS = 0.0001;

/** The server's utils/billXeroPayload.poLineKey, for a PO's line_items element. */
export function poLineKey(li, index) {
  if (!li || typeof li !== 'object') return index == null ? null : `idx:${index}`;
  if (li.kind === 'haulage' && li.haulageForOrderLineId != null) return `haulage:${li.haulageForOrderLineId}`;
  if (li.orderLineId != null && li.orderLineId !== '') return `ol:${li.orderLineId}`;
  if (li.lineItemID) return `li:${li.lineItemID}`;
  return index == null ? null : `idx:${index}`;
}

const words = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const codeOf = (l) => String(l?.product_code || '').trim().toUpperCase();
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};
const has = (v) => v !== undefined && v !== null && String(v).trim() !== '';

// isChargeLine moved to ./chargeLines (2026-10-23) so the server reads a
// bill's haulage by the same rule; re-exported here for every caller.
export { isChargeLine };

/** A Direct Delivery PO's haulage line (or a PO-H's one line), brought in from the PO. */
export const isPoHaulageLine = (l) => l?._poKind === 'haulage' || !!l?._poHaulageLine;

export const isOpenPdfLine = (l) => !!l?._fromPdf && !l?._pdfMatched;
export const isOpenPoLine = (l) => !!l?._fromPo && !l?._pdfMatched;
/** Does this bill line bill a particular PO line (or a PO-H's one haulage line)? */
export const hasPoLine = (l) => !!l?.purchase_order_id && (!!l?.po_line_key || !!l?._poHaulageLine);

/** The PO line, with the invoice line's quantity and price on it. */
export function mergeLines(poLine, pdfLine) {
  // PO → item → bill (utils/billReadLines). A PO line from before `_poTax`
  // was kept carries its tax as tax_type; a read line its bill's as `_billTax`.
  const billTax = pdfLine && '_billTax' in pdfLine ? (pdfLine._billTax?.value ?? null) : (pdfLine?.tax_type ?? null);
  const tax = defaultTaxType({
    poTax: poLine?._poTax !== undefined ? poLine._poTax : poLine?.tax_type,
    itemTax: poLine?._itemTax ?? null,
    billTax,
  });
  return {
    ...poLine,
    quantity: has(pdfLine?.quantity) ? pdfLine.quantity : poLine.quantity,
    unit_price: has(pdfLine?.unit_price) ? pdfLine.unit_price : poLine.unit_price,
    // The invoice's unit goes with its figures (utils/billUnitConversion).
    bill_unit: has(pdfLine?.quantity) ? (pdfLine.bill_unit ?? null) : null,
    tax_type: tax.value,
    tax_rate: tax.value ? (getTaxRate(tax.value) || 0) : (poLine.tax_rate ?? 0),
    _taxFrom: tax.from,
    _billTax: pdfLine?._billTax || null,
    _fromPdf: false,
    _pdfMatched: true,
    _invoiceText: pdfLine?.product_name || null,
    // What the invoice said, for the cells' "PO's or bill's" (utils/billLineFields).
    _bill: pdfLine ? {
      quantity: pdfLine.quantity ?? null,
      unit_price: pdfLine.unit_price ?? null,
      product_name: pdfLine.product_name || '',
      product_code: pdfLine.product_code || '',
      inventory_item_id: pdfLine.inventory_item_id ?? null,
      account_code: pdfLine.account_code || '',
      tax_type: billTax || null,
      tax_rate: billTax ? (getTaxRate(billTax) || 0) : (pdfLine.tax_rate ?? null),
      bill_unit: pdfLine.bill_unit ?? null,
    } : null,
    _highlight: true,
    _hint: null,
  };
}

/** The PO line a PDF line certainly is, among `open`, or null. */
function certainMatch(pdf, openAll) {
  // Goods on the invoice match goods on the PO — never a Direct Delivery
  // PO's haulage line, even when the bill's price includes the haulage.
  // A charge line may take the haulage line when it is the only one open.
  const open = openAll.filter(l => l?._poKind !== 'haulage');
  const code = codeOf(pdf);
  const ddHaulage = openAll.filter(l => l?._poKind === 'haulage');
  if (isChargeLine(pdf.product_name) && ddHaulage.length === 1 && !(code && open.some(l => codeOf(l) === code))) return ddHaulage[0];
  if (code) {
    const byCode = open.filter(l => codeOf(l) === code);
    if (byCode.length === 1) return byCode[0];
    if (byCode.length > 1) {
      const q = num(pdf.quantity);
      const sameQty = byCode.filter(l => q !== null && num(l.quantity) !== null && Math.abs(num(l.quantity) - q) < EPS);
      return sameQty.length === 1 ? sameQty[0] : null;
    }
  }
  const w = words(pdf.product_name);
  if (!w) return null;
  const byWords = open.filter(l => words(l.product_name) === w);
  return byWords.length === 1 ? byWords[0] : null;
}

/**
 * Match every invoice line that is certainly a PO line.
 * @param {object[]} lines — the grid's lines
 * @returns {{ lines: object[], matched: number }}
 */
export function autoMatch(lines) {
  let out = Array.isArray(lines) ? lines.slice() : [];
  let matched = 0;
  for (const pdf of out.filter(isOpenPdfLine)) {
    const target = certainMatch(pdf, out.filter(isOpenPoLine));
    if (!target) continue;
    const merged = mergeLines(target, pdf);
    out = out.filter(l => l.id !== pdf.id).map(l => (l.id === target.id ? merged : l));
    matched += 1;
  }
  return { lines: out, matched };
}

/**
 * The user's own match: this invoice line is that PO line.
 * @returns {object[]} the lines
 */
export function matchLine(lines, pdfLineId, poLineId) {
  const list = Array.isArray(lines) ? lines : [];
  const pdf = list.find(l => l.id === pdfLineId);
  const target = list.find(l => l.id === poLineId);
  if (!pdf || !target || pdf.id === target.id) return list;
  const merged = mergeLines(target, pdf);
  return list.filter(l => l.id !== pdf.id).map(l => (l.id === target.id ? merged : l));
}

/**
 * What is left to reconcile before the bill can be saved.
 * @param {object[]} lines
 * @param {{ active: boolean, isEmpty?: (line: object) => boolean }} opts — active
 *        when the bill was filled from an uploaded document and has POs
 * @returns {{ notOnPo: object[], notOnInvoice: object[], count: number }}
 */
export function reconcileState(lines, { active = false, isEmpty = () => false } = {}) {
  if (!active) return { notOnPo: [], notOnInvoice: [], count: 0 };
  const real = (Array.isArray(lines) ? lines : []).filter(l => !isEmpty(l));
  const notOnInvoice = real.filter(isOpenPoLine);
  // A line kept on purpose with no PO (a surcharge, carriage) is reconciled.
  const notOnPo = real.filter(l => !isOpenPoLine(l) && !hasPoLine(l) && !l._noPo);
  return { notOnPo, notOnInvoice, count: notOnPo.length + notOnInvoice.length };
}

/** A line, in a sentence's words: "Excel Agri Gold Label 18 · 8.01 @ £278.67" (quantity to the quantity rule: "8 " → "8.00"). */
/** Keep an invoice line on the bill with no PO line — a surcharge, carriage, a fee. */
export function keepWithoutPo(lines, lineId) {
  return (Array.isArray(lines) ? lines : []).map(l => (l.id === lineId ? { ...l, _noPo: true } : l));
}

export function lineLabel(l) {
  const what = l?.product_name || l?.product_code || 'A line';
  const q = has(l?.quantity) ? ` · ${formatQuantity(l.quantity) || l.quantity}` : '';
  const p = has(l?.unit_price) ? ` @ £${l.unit_price}` : '';
  return `${what}${q}${p}`;
}
